import { Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Employee } from '@models/employee/employee.model';
import * as Sentry from '@sentry/angular';
import { filter } from 'rxjs/operators';
import { OnboardingStatusService } from '../../services/onboarding-status.service';
import { OnboardingStatus } from '../../services/types';

@Component({
    selector: 'app-self-serve-quickstart',
    templateUrl: './quickstart.template.html',
    styleUrls: ['./quickstart.style.scss'],
})
export class QuickstartComponent {
    isLoading = true;
    onboardingStatus: OnboardingStatus | null = null;

    /**
     * initiallyExpandedStep is the step that will be expanded
     * when the component loads.
     */
    initiallyExpandedStep:
        | 'payrollSituation'
        | 'payYourTeam'
        | 'bookACall'
        | 'payrollDocuments'
        | 'timeOff'
        | 'runFirstPayroll'
        | 'companyDetails'
        | 'addYourTeam'
        | 'inviteYourTeam'
        | null = null;

    stepsCompleted = 0;
    totalSteps = 1;

    /**
     * isQuickstartDone tracks if the company has done everything required for the quickstart.
     * This is to show the completion widget.
     */
    isQuickstartDone = false;
    isKYBEnabled = signal(false);

    constructor(
        private onboardingStatusService: OnboardingStatusService,
        private analyticService: AnalyticService,
        private authService: AuthService,
        private notifyService: NotifyService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private featureService: FeatureService
    ) {
        this.onboardingStatusService.onboardingStatus$
            .pipe(
                filter((onboardingStatus) => onboardingStatus !== null),
                takeUntilDestroyed()
            )
            .subscribe((onboardingStatus) => this.setComponentState(onboardingStatus as OnboardingStatus));
        this.featureService.has(FeatureFlag.KYC_KYB).then(this.isKYBEnabled.set);
    }

    /**
     * TODO: Refactor this function as part of the work for SQE-7776
     * @deprecated
     */
    async sendInvites(): Promise<void> {
        if (!this.onboardingStatus) {
            return;
        }
        // set this temporarily to disable the button in the ui
        this.onboardingStatus.sendInvitations.isComplete = true;

        try {
            const [onboardingEmployees] = await Employee.param('company', this.authService.company.id)
                .where('status', 'onboarding')
                .all();

            const notYetInvitedEmployees = onboardingEmployees.filter((e) => e.isOnboardingPending);
            if (notYetInvitedEmployees.length) {
                const accountIds: number[] = [];
                notYetInvitedEmployees.forEach((e) => e.accountId && accountIds.push(e.accountId));
                await this.authService.sendInvitations(accountIds);
                this.notifyService.success('self-serve.quickstart.onboardingInvitesSentToEmployees');
            }

            this.authService.company.selfServeQuickstart.invitationsSentAt = new Date();
            await this.authService.company.selfServeQuickstart.save();

            this.analyticService.trackEvent(AnalyticEvents.SelfServeInviteYourTeamClickGo);
        } catch (error) {
            this.notifyService.error(error);
        } finally {
            this.onboardingStatusService.refresh();
        }
    }

    /**
     * navigateTo is a hack. I need to perform an action when a link is clicked, but also then
     * navigate to this link. Angular doesn't seem to make this easy.
     */
    async navigateTo(
        navigable:
            | 'pay-your-team'
            | 'book-a-call'
            | 'payroll-documents'
            | 'company-details'
            | 'add-your-team'
            | 'time-off'
    ): Promise<void> {
        // try to update the quickstart timestamp
        const quickstart = this.authService.company.selfServeQuickstart;
        try {
            switch (navigable) {
                case 'pay-your-team':
                    this.analyticService.trackEvent(AnalyticEvents.SelfServeGetReadyToPayYourTeamClickGo);
                    if (!quickstart.payYourTeamStartedAt) {
                        quickstart.payYourTeamStartedAt = new Date();
                        quickstart.save();
                    }
                    break;
                case 'book-a-call':
                    this.analyticService.trackEvent(AnalyticEvents.SelfServeBookYourPayrollCallClickGo);
                    if (!quickstart.bookACallStartedAt) {
                        quickstart.bookACallStartedAt = new Date();
                        quickstart.save();
                    }
                    break;
                case 'payroll-documents':
                    this.analyticService.trackEvent(AnalyticEvents.SelfServePrepareDocumentsClickGo);
                    if (!quickstart.payrollDocumentsStartedAt) {
                        quickstart.payrollDocumentsStartedAt = new Date();
                        quickstart.save();
                    }
                    break;
                case 'company-details':
                    this.analyticService.trackEvent(AnalyticEvents.SelfServeAddCompanyDetailsClickContinue);
                    if (!quickstart.companyDetailsStartedAt) {
                        quickstart.companyDetailsStartedAt = new Date();
                        quickstart.save();
                    }
                    break;
                case 'add-your-team':
                    this.analyticService.trackEvent(AnalyticEvents.SelfServeAddTeamClickGo);
                    if (!quickstart.addYourTeamStartedAt) {
                        quickstart.addYourTeamStartedAt = new Date();
                        quickstart.save();
                    }
                    break;
                case 'time-off':
                    this.analyticService.trackEvent(AnalyticEvents.SelfServeSetUpTimeOffClickGo);
                    if (!quickstart.timeOffStartedAt) {
                        quickstart.timeOffStartedAt = new Date();
                        quickstart.save();
                    }
                    break;
            }
        } catch (error) {
            Sentry.captureException(error);
        }

        switch (navigable) {
            case 'pay-your-team':
            case 'book-a-call':
            case 'payroll-documents':
            case 'company-details':
            case 'add-your-team':
                this.router.navigate([navigable], { relativeTo: this.activatedRoute });
                break;
            case 'time-off':
                this.router.navigate(['/time-off/v3/types-and-policies']);
                break;
        }
    }

    /**
     * dismissQuickstart handles a user no longer wanting to see the quickstart guide.
     * This can only be done once the guide is complete.
     *
     * To make the dismissal feel faster, we update the UI optimistically (assuming that API calls won't fail)
     * so that the user doesn't have to watch a bunch of loading spinners.
     */
    async dismissQuickstart(): Promise<void> {
        this.isQuickstartDone = false;
        this.isLoading = true;

        const quickstart = this.authService.company.selfServeQuickstart;
        quickstart.completedAt = new Date();

        quickstart
            .save()
            .then(() => this.onboardingStatusService.refresh())
            .catch((error) => this.notifyService.error(error));

        // we have dispatched the completion to the back end and have the local value in memory
        // so we can navigate now and expect the backend will not error
        this.router.navigate(['/']);
    }

    onAddMyBenefitsClick(): void {
        this.analyticService.trackEvent(AnalyticEvents.SelfServeBenefitsCardClickLink);
        this.router.navigate(['/benefits']);
    }

    trackSelfServeEditPayroll(): void {
        this.analyticService.trackEvent(AnalyticEvents.SelfServeEditPayrollSituationClickEdit);
        this.router.navigate(['/company-onboarding', 'payroll-situation']);
    }

    trackSelfServeTimeOffClickSetItUp(): void {
        this.analyticService.trackEvent(AnalyticEvents.SelfServeTimeOffClickSetItUp);
        this.router.navigate(['/time-off', 'v3', 'types-and-policies']);
    }

    private setComponentState(onboardingStatus: OnboardingStatus): void {
        try {
            this.onboardingStatus = onboardingStatus;

            this.stepsCompleted = onboardingStatus.stepsCompleted;
            this.totalSteps = onboardingStatus.totalSteps;
            this.isQuickstartDone = this.stepsCompleted === this.totalSteps;

            this.setInitiallyExpandedStep();
        } catch (error) {
            this.notifyService.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    private setInitiallyExpandedStep(): void {
        if (!this.onboardingStatus?.quickstartType) {
            return;
        }

        const quickstartType = this.onboardingStatus.quickstartType;

        if (quickstartType === 'HR_PAYROLL' || quickstartType === 'HR_PAYROLL_TIMEOFF') {
            if (this.onboardingStatus.payYourTeam.percentageComplete !== 100) {
                this.initiallyExpandedStep = 'payYourTeam';
                return;
            }
            if (this.onboardingStatus.bookACall.percentageComplete !== 100) {
                this.initiallyExpandedStep = 'bookACall';
                return;
            }
            if (!this.onboardingStatus.payrollDocuments.isComplete) {
                this.initiallyExpandedStep = 'payrollDocuments';
                return;
            }
            if (quickstartType === 'HR_PAYROLL_TIMEOFF' && this.onboardingStatus.timeOff.percentageComplete !== 100) {
                this.initiallyExpandedStep = 'timeOff';
                return;
            }
            if (!this.onboardingStatus.runFirstPayroll.isComplete) {
                this.initiallyExpandedStep = 'runFirstPayroll';
                return;
            }
        }

        if (quickstartType === 'HR_TIMEOFF') {
            if (this.onboardingStatus.companyDetails.percentageComplete !== 100) {
                this.initiallyExpandedStep = 'companyDetails';
                return;
            }
            if (this.onboardingStatus.addYourTeam.percentageComplete !== 100) {
                this.initiallyExpandedStep = 'addYourTeam';
                return;
            }
            if (this.onboardingStatus.timeOff.percentageComplete !== 100) {
                this.initiallyExpandedStep = 'timeOff';
                return;
            }
            if (!this.onboardingStatus.sendInvitations.isComplete) {
                this.initiallyExpandedStep = 'inviteYourTeam';
                return;
            }
        }
    }
}
